/* Fit checker drawings: pieces read from the product's specifications text and drawn as clean SVG illustrations
   in the selected finish. Used by nt-fit-checker.js when the section's "Piece pictures" setting is "Drawings". */
(() => {
  if (window.NtFitDraw) return;

  /* ---------- colours ---------- */
  const parseColors = (text) => {
    const map = {};
    String(text || '').split(/\n|<br\s*\/?>/i).forEach((line) => {
      const i = line.indexOf(':');
      if (i < 1) return;
      const k = line.slice(0, i).trim().toLowerCase();
      const v = line.slice(i + 1).trim();
      if (k && /^#?[0-9a-f]{3,8}$/i.test(v)) map[k] = v.startsWith('#') ? v : '#' + v;
    });
    return map;
  };
  const shade = (hex, amt) => {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h.slice(0, 6), 16);
    const f = (c) => Math.max(0, Math.min(255, Math.round(c + (amt < 0 ? c * amt : (255 - c) * amt))));
    const r = f(n >> 16), g = f((n >> 8) & 255), b = f(n & 255);
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };
  /* "White and Oak" -> body White, fronts Oak. Unknown -> neutral. */
  const finishTones = (finish, colors) => {
    const f = String(finish || '').toLowerCase().trim();
    const tone = (name) => ({ hex: colors[name] || '#D9D4CB', wood: /oak|wood|walnut|pine/.test(name) });
    if (colors[f]) return { body: tone(f), front: tone(f) };
    const parts = f.split(/\s+(?:and|&)\s+|\s*-\s*/).filter(Boolean);
    if (parts.length === 2) return { body: tone(parts[0]), front: tone(parts[1]) };
    return { body: tone(f), front: tone(f) };
  };

  /* ---------- specifications text -> pieces ---------- */
  const toText = (html) => {
    const s = String(html || '').replace(/[\r\n]+/g, ' ')
      .replace(/<br[^>]*>/gi, '\n').replace(/<\/(p|tr|li|div|h\d)>/gi, '\n').replace(/<hr[^>]*>/gi, '\n')
      .replace(/<\/t[dh]>/gi, ' | ').replace(/<[^>]+>/g, '');
    const t = document.createElement('textarea'); t.innerHTML = s; return t.value;
  };
  const num = (s) => parseFloat(String(s).replace(',', '.'));
  const listNums = (s) => String(s).split(/[–—\-\/]/).map(num).filter((n) => !isNaN(n));

  const kindOf = (name, d) => {
    const n = (name || '').toLowerCase();
    if (/basin|sink/.test(n) && !/cabinet|unit|vanity/.test(n)) return 'basin';
    if (/mirror/.test(n)) return 'mirror';
    if (/towel|tall|column|tower|hanging (tall|wall) unit|storage cupboard|side cabinet|utility/.test(n)) return 'tall';
    if (/top cupboard|top cabinet|wall unit|upper|wall cabinet/.test(n)) return 'wall';
    if (/washing|washer|dryer|laundry|stacker/.test(n)) return 'washer';
    if (/vanity|basin|cabinet|unit/.test(n)) return 'vanity';
    if (d) {
      if (d.h && d.h < 16) return 'basin';
      if (d.d && d.d <= 20) return 'mirror';
      if (d.h >= 100) return 'tall';
    }
    return n ? 'vanity' : '';
  };

  const pickW = (widths, size) => {
    if (widths.length < 2) return widths[0];
    const s = num(String(size || '').replace(/[^\d.,]/g, ' ').trim().split(/\s+/)[0]);
    if (!isNaN(s) && widths.includes(s)) return s;
    return widths[0];
  };

  const parseSpec = (html, size) => {
    const lines = toText(html).split('\n').map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean);
    const out = [];
    let header = null;
    for (const line of lines) {
      /* table rows */
      if (line.includes('|')) {
        const cells = line.split('|').map((c) => c.trim()).filter((c, i, a) => !(c === '' && i === a.length - 1));
        const low = cells.map((c) => c.toLowerCase());
        if (low.some((c) => c === 'width') && low.some((c) => c === 'height')) { header = low; continue; }
        if (header) {
          const get = (k) => { const i = header.indexOf(k); return i >= 0 ? num(cells[i]) : NaN; };
          const w = get('width'), h = get('height'), d = get('depth');
          const nameCell = cells.find((c, i) => isNaN(num(c)) && c) || '';
          const sizeCol = header.indexOf('size');
          if (sizeCol >= 0 && size) { const sv = num(cells[sizeCol]); const ss = num(String(size).replace(/[^\d.]/g, '')); if (!isNaN(sv) && !isNaN(ss) && sv !== ss) continue; }
          if (w > 0 && h > 0) out.push({ name: nameCell, w, h, d, kind: kindOf(nameCell, { w, h, d }) });
          continue;
        }
      }
      /* "Name = H 45cm x W 65–75–95cm x D 45cm"  /  "HWD: 185 x 67 x 65 cm" */
      let name = '', rest = line;
      const eq = line.match(/^(.*?)\s*[=:]\s*(?=H\b|H\s*\d|HWD|\d)/i);
      if (eq) { name = eq[1].trim(); rest = line.slice(eq[0].length); }
      /* variant labels such as "V1 - HWD" are not piece names */
      if (/^(v\d+|option \d+)?\s*[-–]?\s*(hwd)?$/i.test(name)) name = '';
      let w, h, d;
      const hwd = line.match(/HWD\s*:?\s*([\d.,]+)\s*[x×]\s*([\d.,]+)\s*[x×]\s*([\d.,]+)/i);
      if (hwd) { h = num(hwd[1]); w = num(hwd[2]); d = num(hwd[3]); }
      else {
        const lab = (L) => { const m = rest.match(new RegExp('\\b' + L + '\\s*([\\d.,]+(?:\\s*[–—-]\\s*[\\d.,]+)*)\\s*(?:cm)?', 'i')); return m ? listNums(m[1]) : null; };
        const after = (L) => { const m = rest.match(new RegExp('([\\d.,]+)\\s*(?:cm)?\\s*\\(\\s*' + L + '\\s*\\)', 'i')); return m ? [num(m[1])] : null; };
        const W = lab('W') || after('W'), H = lab('H') || after('H'), D = lab('D') || after('D');
        if (W && H) { w = pickW(W, size); h = H[0]; d = D ? D[0] : NaN; }
      }
      if (w > 0 && h > 0) { if (!name) name = ''; out.push({ name, w, h, d, kind: kindOf(name, { w, h, d }) }); }
    }
    /* Several unnamed parts (e.g. Likya set): the first unnamed one is the vanity. */
    if (out.filter((p) => p.kind !== 'basin').length > 1) {
      const first = out.find((p) => !p.kind);
      if (first) first.kind = 'vanity';
    }
    /* Size variants listed as separate lines (e.g. 60 / 90 / 150 cm towel cabinets, 80 / 60 cm mirrors): keep the selected one. */
    const real = out.filter((p) => p.kind !== 'basin');
    if (real.length > 1 && real.every((p) => (p.kind || '') === (real[0].kind || '')) && !real.some((p) => p.kind === 'vanity' && real.length === 2 && false)) {
      const sn = num(String(size || '').replace(/[^\d.,]/g, ' ').trim().split(/\s+/)[0]);
      const names = new Set(real.map((p) => p.name.toLowerCase().replace(/\b(mini|short|tall|small|large|wide)\b/g, '').trim()).filter(Boolean));
      if (names.size <= 1) {
        const nums = (String(size || '').match(/[\d.]+/g) || []).map(Number);
        const hit = real.find((p) => nums.includes(p.w) && nums.includes(p.h)) || (!isNaN(sn) ? real.find((p) => p.w === sn || p.h === sn) : null);
        const keep = hit || real[0];
        return out.filter((p) => p === keep || p.kind === 'basin');
      }
    }
    /* "Height: 180 cm" / "Width: 70 cm" on separate lines -> one piece */
    if (!out.length) {
      const grab = (k) => { const l = lines.find((x) => new RegExp('^[-•\\s]*' + k + '\\s*:', 'i').test(x)); return l ? num(l.split(':')[1]) : NaN; };
      const w = grab('Width'), h = grab('Height'), d = grab('Depth');
      if (w > 0 && h > 0) out.push({ name: '', w, h, d, kind: '' });
    }
    return out;
  };

  /* Build fit pieces (row + above) from parsed spec parts. */
  const buildPieces = (parts, opts) => {
    const B = opts.bottoms || {};
    const basin = parts.find((p) => p.kind === 'basin');
    const list = [];
    const labels = opts.labels || {};
    parts.forEach((p) => {
      if (p.kind === 'basin') return;
      let kind = p.kind || opts.guess || 'vanity';
      /* laundry / utility cabinets stand on the floor */
      if (opts.guess === 'washer' && (kind === 'tall' || kind === 'vanity')) kind = 'washer';
      const piece = { name: labels[kind] || p.name || kind, w: p.w, body: p.h, kind, optional: false, above: false };
      if (kind === 'vanity') {
        piece.basin = basin ? Math.min(basin.w, p.w) : (opts.hasBasin ? Math.min(p.w * 0.82, 61) : 0);
        piece.basinH = basin ? Math.min(Math.max(basin.h, 8), 18) : 12;
        piece.h = p.h + (piece.basin ? piece.basinH + 9 : 0);
        piece.bottom = B.vanity;
      } else if (kind === 'mirror' || kind === 'wall') {
        piece.h = p.h; piece.bottom = B.mirror; piece.above = true;
      } else if (kind === 'tall') {
        piece.h = p.h; piece.bottom = B.tall;
      } else {
        piece.h = p.h; piece.bottom = 0;
      }
      list.push(piece);
    });
    /* Above pieces sit over the vanity; tall units in a set may go on another wall. */
    const row = list.filter((p) => !p.above);
    if (row.length > 1) row.forEach((p) => { if (p.kind === 'tall') p.optional = true; });
    const vanity = list.find((p) => p.kind === 'vanity');
    const above = list.filter((p) => p.above);
    const ordered = [];
    list.filter((p) => !p.above).forEach((p) => { ordered.push(p); if (p === vanity) ordered.push(...above); });
    if (!vanity) ordered.push(...above.map((p) => ({ ...p, above: false, bottom: p.bottom })));
    return ordered;
  };

  /* ---------- SVG drawings (viewBox in cm, so every piece is to scale) ---------- */
  let uid = 0;
  const woodDefs = (id, hex) => `<pattern id="${id}" width="6" height="40" patternUnits="userSpaceOnUse"><rect width="6" height="40" fill="${hex}"/><path d="M1 0v40M3.6 0c.6 12-.6 24 0 40M5.2 0v40" stroke="${shade(hex, -0.16)}" stroke-width=".35" opacity=".55" fill="none"/></pattern>`;
  const paint = (tone, defs) => {
    if (!tone.wood) return tone.hex;
    const id = 'raw' + (++uid); defs.push(woodDefs(id, tone.hex)); return `url(#${id})`;
  };

  /* ---------- structure drawings ([ns].fit_drawing): a carcass split into rows / cells read off the product photos ----------
     piece: { w, h (carcass cm), bottom (hangs at cm), sides, top, base (frame thickness), div (divider cm), feet, plinth, swap (fronts/body colours swapped),
              handle ('bar'|'hbar'|'knob'|'grip'|'none'), handleColor, worktop {h, over}, basin {shape:'vessel'|'rect'|'top', w, h, x, tap:'deck'|'wall'|'none'},
              rows: [ { h, cells: [ { w, t, rows?, shelves, appliance, basket, hinge, handle, handlePos, fill } ] } ] }
     t: open | drawer | tray | door | flap | mirror | glass | panel | gap */
  const machine = (x, y, w, h, type) => {
    const mw = Math.min(w - 1.5, 60), mh = Math.min(h - 1, 85);
    const mx = x + (w - mw) / 2, my = y + h - mh;
    const r = Math.min(mw, mh) * 0.3, cx = mx + mw / 2, cy = my + mh * 0.58;
    return `<g><rect x="${mx}" y="${my}" width="${mw}" height="${mh}" rx="1.6" fill="#FBFBFA" stroke="rgba(20,19,15,.3)" stroke-width=".35"/>`
      + `<rect x="${mx + 2}" y="${my + 2}" width="${mw * 0.32}" height="${mh * 0.07}" rx=".6" fill="#EDEDEB"/>`
      + `<rect x="${mx + mw - 12}" y="${my + 2.2}" width="9" height="${mh * 0.055}" rx=".5" fill="#2A2A2A"/>`
      + `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#E7E7E5" stroke="rgba(20,19,15,.35)" stroke-width=".5"/>`
      + `<circle cx="${cx}" cy="${cy}" r="${r * 0.72}" fill="${type === 'dryer' ? '#B9BEC2' : '#3E4A57'}"/>`
      + `<path d="M${cx - r * 0.45} ${cy - r * 0.2} q${r * 0.3} ${-r * 0.35} ${r * 0.7} ${-r * 0.25}" stroke="#fff" stroke-width=".6" opacity=".5" fill="none"/></g>`;
  };
  const basinUp = (b) => {
    if (!b) return 0;
    const h = b.h || 12, tap = b.tap || 'deck';
    return h + (tap === 'deck' ? (b.shape === 'vessel' || b.shape === 'bowl' ? 13 : 7) : 0) - (b.shape === 'rect' || !b.shape ? 3 : 0);
  };

  const drawLayout = (p, tones0) => {
    const L = p.layout, W = p.w, defs = [];
    let tones = L.swap ? { body: tones0.front, front: tones0.body } : tones0;
    /* solid: body and fronts both take the finish's non-wood colour (wood only as 'oak' accents) */
    if (L.solid) { const nw = !tones0.front.wood ? tones0.front : !tones0.body.wood ? tones0.body : tones0.body; tones = { body: nw, front: nw }; }
    /* woodBody: the carcass takes the finish's wood tone, fronts the other one */
    if (L.woodBody) { const w = tones0.body.wood ? tones0.body : tones0.front.wood ? tones0.front : null; if (w) tones = { body: w, front: w === tones0.body ? tones0.front : tones0.body }; }
    /* fixed carcass colour (e.g. always oak): fronts take the finish's non-wood colour */
    if (L.body) tones = { body: { hex: L.body, wood: L.bodyWood !== false }, front: !tones0.front.wood ? tones0.front : !tones0.body.wood ? tones0.body : tones0.front };
    const body = p.body || p.h;
    const fills = { body: paint(tones.body, defs), front: paint(tones.front, defs) };
    const fillOf = (v, dflt) => !v ? dflt : v === 'body' ? fills.body : v === 'front' ? fills.front : v === 'white' ? '#F4F3F0' : v === 'black' ? '#2E2D2A' : v === 'oak' ? paint({ hex: '#B8905F', wood: true }, defs) : /^#/.test(v) ? v : dflt;
    const inside = shade(tones.body.hex, -0.13);
    const hcol = L.handleColor || '#34322E';
    const line = 'stroke="rgba(20,19,15,.3)" stroke-width=".35"';
    const side = L.sides != null ? L.sides : 1.8, top = L.top != null ? L.top : side, bot = L.base != null ? L.base : side;
    const div = L.div != null ? L.div : 1.8, feet = L.feet || 0, plinth = L.plinth || 0;
    const wt = L.worktop ? (L.worktop.h || 3) : 0, over = L.worktop ? (L.worktop.over || 0) : 0;
    const bx = over, bw = W - 2 * over, by = wt, bh = body - wt - feet - plinth;
    let g = '';
    if (wt) g += `<rect x="0" y="0" width="${W}" height="${wt}" fill="${fillOf(L.worktop.fill, fills.body)}" ${line}/>`;
    g += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" fill="${fills.body}" ${line}/>`;
    if (plinth) g += `<rect x="${bx + 1}" y="${by + bh}" width="${bw - 2}" height="${plinth}" fill="${shade(tones.body.hex, -0.3)}"/>`;
    if (feet) {
      const fw = L.feetW || 2.5, fc = L.feetColor || shade(tones.body.hex, -0.45);
      g += `<rect x="${bx + 1.5}" y="${body - feet}" width="${fw}" height="${feet}" fill="${fc}"/><rect x="${bx + bw - 1.5 - fw}" y="${body - feet}" width="${fw}" height="${feet}" fill="${fc}"/>`;
    }
    const handleOf = (c) => c.handle || L.handle || 'bar';
    const drawHandle = (kind, x, y, w, h, c, isDrawer) => {
      const hs = handleOf(c);
      if (hs === 'none') return '';
      if (hs === 'grip') return `<rect x="${x + 0.6}" y="${y + 0.4}" width="${w - 1.2}" height="1.1" fill="rgba(20,19,15,.22)"/>`;
      const pos = c.handlePos || L.handlePos || (isDrawer ? 'middle' : 'top');
      if (isDrawer || kind === 'flap' || hs === 'hbar') {
        const hy = pos === 'top' ? y + 2.5 : pos === 'bottom' ? y + h - 3.5 : y + h / 2 - 0.45;
        if (hs === 'knob') return `<circle cx="${x + w / 2}" cy="${hy + 0.45}" r="1.1" fill="${hcol}"/>`;
        const hw = c.handleW || Math.min(w * 0.35, 16);
        const ha = c.handleAlign || L.handleAlign;
        const hx0 = ha === 'right' ? x + w - hw - 2.5 : ha === 'left' ? x + 2.5 : x + (w - hw) / 2;
        return `<rect x="${hx0}" y="${hy}" width="${hw}" height=".9" rx=".45" fill="${hcol}"/>`;
      }
      const hinge = c.hinge || 'left';
      const hx = hinge === 'left' ? x + w - 2.6 : x + 1.8;
      if (hs === 'knob') {
        const ky = pos === 'bottom' ? y + h - 4 : pos === 'middle' ? y + h / 2 : y + 4;
        return `<circle cx="${hinge === 'left' ? x + w - 3 : x + 3}" cy="${ky}" r="1.1" fill="${hcol}"/>`;
      }
      const hh = Math.min(12, h * 0.25);
      const hy = pos === 'bottom' ? y + h - hh - 3 : pos === 'middle' ? y + h / 2 - hh / 2 : y + 3;
      return `<rect x="${hx}" y="${hy}" width=".8" height="${hh}" rx=".4" fill="${hcol}"/>`;
    };
    const mirrorFill = () => {
      const gid = 'ram' + (++uid);
      defs.push(`<linearGradient id="${gid}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#E9EEF0"/><stop offset=".55" stop-color="#C9D2D6"/><stop offset="1" stop-color="#DDE3E5"/></linearGradient>`);
      return `url(#${gid})`;
    };
    /* cell at x,y,w,h; ed = which sides touch the carcass edge (fronts overlap dividers / edges) */
    const cell = (c, x, y, w, h, ed) => {
      if (c.rows) return grid(c.rows, x, y, w, h, ed);
      const t = c.t || 'open';
      if (t === 'gap') return '';
      if (t === 'open' || t === 'shelves' || t === 'basket') {
        let s = `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${c.back === 'oak' ? paint({ hex: '#B8905F', wood: true }, defs) : fillOf(c.back, inside)}" ${line}/>`;
        s += `<rect x="${x}" y="${y}" width="${w}" height="${Math.min(2, h * 0.08)}" fill="rgba(20,19,15,.12)"/>`;
        const n = c.shelves || 0;
        for (let i = 1; i <= n; i++) { const sy = y + h * i / (n + 1); s += `<rect x="${x}" y="${sy - 0.9}" width="${w}" height="1.8" fill="${fills.body}" ${line}/>`; }
        if (c.appliance) s += machine(x, y, w, h, c.appliance);
        if (c.basket || t === 'basket') { const bw2 = Math.min(w - 3, 42), bh2 = Math.min(h * 0.75, c.basketH || 30); s += `<rect x="${x + (w - bw2) / 2}" y="${y + h - bh2}" width="${bw2}" height="${bh2}" rx="1.5" fill="#E4DCCB" stroke="rgba(20,19,15,.25)" stroke-width=".35"/><path d="M${x + (w - bw2) / 2 + 2} ${y + h - bh2 + 3}h${bw2 - 4}" stroke="rgba(20,19,15,.18)" stroke-width=".5"/>`; }
        if (c.rail) s += `<rect x="${x + 1}" y="${y + 4}" width="${w - 2}" height=".8" rx=".4" fill="#9A9894"/>`;
        return s;
      }
      if (t === 'panel') return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fillOf(c.fill, fills.body)}" ${line}/>`;
      /* fronts cover the dividers and most of the carcass edge */
      const o = (e) => (e ? Math.max(0, (e === 'side' ? side : e === 'top' ? top : bot) - 0.25) : div / 2 - 0.2);
      const fx = x - (c.inset ? 0 : o(ed.l && 'side')), fy = y - (c.inset ? 0 : o(ed.t && 'top'));
      const fw = w + (c.inset ? 0 : o(ed.l && 'side') + o(ed.r && 'side')), fh = h + (c.inset ? 0 : o(ed.t && 'top') + o(ed.b && 'bot'));
      if (t === 'mirror') {
        let s = `<rect x="${fx}" y="${fy}" width="${fw}" height="${fh}" fill="${mirrorFill()}" ${line}/>`;
        s += `<path d="M${fx + fw * 0.15} ${fy + fh * 0.62} L${fx + fw * 0.62} ${fy + 2}" stroke="#fff" stroke-width="${Math.max(1, fw * 0.06)}" opacity=".45"/>`;
        if (c.handle && c.handle !== 'none') s += drawHandle('door', fx, fy, fw, fh, c, false);
        return s;
      }
      if (t === 'glass') {
        let s = `<rect x="${fx}" y="${fy}" width="${fw}" height="${fh}" fill="${fillOf(c.fill, fills.front)}" ${line}/>`;
        s += `<rect x="${fx + 3}" y="${fy + 3}" width="${fw - 6}" height="${fh - 6}" fill="rgba(206,216,220,.75)" ${line}/>`;
        return s + drawHandle('door', fx, fy, fw, fh, c, false);
      }
      let s = `<rect x="${fx}" y="${fy}" width="${fw}" height="${fh}" fill="${fillOf(c.fill, fills.front)}" ${line}/>`;
      return s + drawHandle(t, fx, fy, fw, fh, c, t === 'drawer' || t === 'tray');
    };
    const grid = (rows, x, y, w, h, ed) => {
      let s = '';
      const total = rows.reduce((a, r) => a + (r.h || 1), 0) || 1;
      const avail = h - div * (rows.length - 1);
      let cy = y;
      rows.forEach((r, ri) => {
        const rh = avail * (r.h || 1) / total;
        const cells = r.cells || [{ t: 'open' }];
        const cwT = cells.reduce((a, c) => a + (c.w || 1), 0);
        const availW = w - div * (cells.length - 1);
        let cx = x;
        const rowEd = { t: ed.t && ri === 0, b: ed.b && ri === rows.length - 1 };
        if (div > 0 && ri > 0) s += `<rect x="${x}" y="${cy - div}" width="${w}" height="${div}" fill="${fills.body}"/>`;
        cells.forEach((c, ci) => {
          const cw = availW * (c.w || 1) / cwT;
          s += cell(c, cx, cy, cw, rh, { l: ed.l && ci === 0, r: ed.r && ci === cells.length - 1, t: rowEd.t, b: rowEd.b });
          cx += cw + div;
        });
        cy += rh + div;
      });
      return s;
    };
    g += grid(L.rows || [], bx + side, by + top, bw - 2 * side, bh - top - bot, { l: true, r: true, t: true, b: true });
    /* basin + tap on the carcass top */
    let extra = '';
    const B = L.basin;
    if (B) {
      const bwid = Math.min(B.w || W * 0.8, W), bhgt = B.h || 12, cx = B.x != null ? B.x : W / 2, x0 = cx - bwid / 2;
      const shape = B.shape || 'rect', tap = B.tap || 'deck';
      let tapX = B.tapX != null ? B.tapX : cx, baseY;
      if (shape === 'vessel' || shape === 'bowl') {
        const r = shape === 'bowl' ? Math.min(bhgt * 0.95, bwid / 2) : Math.min(bhgt * 0.6, bwid / 2);
        extra += `<path d="M${x0} ${-bhgt} H${x0 + bwid} V${-r} Q${x0 + bwid} 0 ${x0 + bwid - r} 0 H${x0 + r} Q${x0} 0 ${x0} ${-r} Z" fill="#FFFFFF" stroke="rgba(20,19,15,.25)" stroke-width=".4"/>`;
        extra += `<rect x="${x0 + 1.5}" y="${-bhgt + 0.8}" width="${bwid - 3}" height="1.2" rx=".6" fill="#ECEAE6"/>`;
        baseY = -bhgt;
        if (tap === 'deck') extra = `<rect x="${cx - 0.8}" y="${-bhgt - 12}" width="1.6" height="${bhgt + 12}" fill="${B.tapColor || '#3A3935'}"/><rect x="${cx - 0.8}" y="${-bhgt - 12}" width="5.5" height="1.6" rx=".8" fill="${B.tapColor || '#3A3935'}"/>` + extra;
      } else if (shape === 'top') {
        extra += `<rect x="${x0}" y="${-bhgt}" width="${bwid}" height="${bhgt}" rx="1.2" fill="#FFFFFF" stroke="rgba(20,19,15,.25)" stroke-width=".4"/>`;
        extra += `<path d="M${x0 + 4} ${-bhgt + 1.6} H${x0 + bwid - 4}" stroke="#E4E2DE" stroke-width="1"/>`;
        baseY = -bhgt;
        if (tap === 'deck') extra += `<path d="M${tapX - 0.9} ${baseY} v-6.5 h5 v1.6 h-3.4 v4.9 z" fill="${B.tapColor || '#B9B7B2'}"/>`;
      } else {
        extra += `<rect x="${x0}" y="${-bhgt + 3}" width="${bwid}" height="${bhgt}" rx="2.2" fill="#FFFFFF" stroke="rgba(20,19,15,.22)" stroke-width=".4"/>`;
        baseY = -bhgt + 3;
        if (tap === 'deck') extra += `<path d="M${tapX - 0.9} ${baseY} v-6.5 h5 v1.6 h-3.4 v4.9 z" fill="${B.tapColor || '#B9B7B2'}"/>`;
      }
    }
    const up = basinUp(B);
    return `<svg viewBox="0 ${-up} ${W} ${body + up}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><defs>${defs.join('')}</defs>${g}${extra}</svg>`;
  };

  const draw = (p, tones) => {
    if (p.layout) return drawLayout(p, tones);
    const W = p.w, H = p.h, defs = [];
    const bodyFill = paint(tones.body, defs), frontFill = paint(tones.front, defs);
    const edge = shade(tones.body.hex, -0.28);
    const line = 'stroke="rgba(20,19,15,.28)" stroke-width=".35"';
    let g = '';
    const doors = (x, y, w, h, n, fill, handles) => {
      let s = '';
      const dw = (w - (n - 1) * 0.4) / n;
      for (let i = 0; i < n; i++) {
        const dx = x + i * (dw + 0.4);
        s += `<rect x="${dx}" y="${y}" width="${dw}" height="${h}" fill="${fill}" ${line}/>`;
        if (handles) { const hx = n > 1 ? (i === 0 ? dx + dw - 2.2 : dx + 1.4) : dx + dw - 2.2; s += `<rect x="${hx}" y="${y + 3}" width=".8" height="${Math.min(10, h * 0.28)}" rx=".4" fill="${edge}" opacity=".7"/>`; }
      }
      return s;
    };
    if (p.kind === 'vanity') {
      const body = p.body, top = H - body;
      g += `<rect x="0" y="${top}" width="${W}" height="${body}" fill="${bodyFill}" ${line}/>`;
      const n = W >= 56 ? 2 : 1;
      g += doors(1, top + 1.5, W - 2, body - 3, n, frontFill, true);
      if (p.basin) {
        const bw = p.basin, bh = p.basinH, bx = (W - bw) / 2, by = top - bh + 3;
        g += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="2.2" fill="#FFFFFF" stroke="rgba(20,19,15,.22)" stroke-width=".4"/>`;
        g += `<rect x="${bx + 2.5}" y="${by + 1.2}" width="${bw - 5}" height="1.4" rx=".7" fill="#ECEAE6"/>`;
        const tx = W / 2;
        g += `<path d="M${tx - 0.9} ${by} v-6.5 h5 v1.6 h-3.4 v4.9 z" fill="${B.tapColor || '#B9B7B2'}"/>`;
      }
    } else if (p.kind === 'mirror') {
      g += `<rect x="0" y="0" width="${W}" height="${H}" fill="${bodyFill}" ${line}/>`;
      const n = W >= 50 ? 2 : 1, gap = 0.5, pad = 1.2, dw = (W - 2 * pad - (n - 1) * gap) / n;
      const gid = 'ram' + (++uid);
      defs.push(`<linearGradient id="${gid}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#E9EEF0"/><stop offset=".55" stop-color="#C9D2D6"/><stop offset="1" stop-color="#DDE3E5"/></linearGradient>`);
      for (let i = 0; i < n; i++) {
        const x = pad + i * (dw + gap);
        g += `<rect x="${x}" y="${pad}" width="${dw}" height="${H - 2 * pad}" fill="url(#${gid})" ${line}/>`;
        g += `<path d="M${x + dw * 0.15} ${pad + (H - 2 * pad) * 0.62} L${x + dw * 0.62} ${pad + 2}" stroke="#fff" stroke-width="${Math.max(1, dw * 0.06)}" opacity=".45"/>`;
      }
    } else if (p.kind === 'tall' || p.kind === 'washer') {
      g += `<rect x="0" y="0" width="${W}" height="${H}" fill="${bodyFill}" ${line}/>`;
      const n = W >= 56 ? 2 : 1;
      if (p.kind === 'washer' && H > 120) {
        const split = H * 0.42;
        g += doors(1, 1.5, W - 2, split - 2.5, n, frontFill, true);
        g += doors(1, split + 0.5, W - 2, H - split - 2, n, frontFill, true);
      } else {
        const split = H > 90 ? H * 0.46 : 0;
        if (split) { g += doors(1, 1.5, W - 2, split - 2, n, frontFill, true); g += doors(1, split + 0.2, W - 2, H - split - 1.7, n, frontFill, true); }
        else g += doors(1, 1.5, W - 2, H - 3, n, frontFill, true);
      }
    } else {
      g += `<rect x="0" y="0" width="${W}" height="${H}" fill="${bodyFill}" ${line}/>`;
      g += doors(1, 1.5, W - 2, H - 3, W >= 56 ? 2 : 1, frontFill, true);
    }
    return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><defs>${defs.join('')}</defs>${g}</svg>`;
  };

  /* Merge a product's fit_drawing into pieces. JSON: {"pieces":[{rows, basin, sides, feet, w?, h?, bottom?, kind?, name?, above?, optional?}], "variants":{"<text in size>":{"pieces":[...]}}} */
  const applyLayout = (pieces, spec, size, opts) => {
    if (!spec || !spec.pieces) return pieces;
    let use = spec;
    if (spec.variants && size) for (const k in spec.variants) { if (String(size).toLowerCase().includes(k.toLowerCase())) { use = { ...spec, ...spec.variants[k] }; break; } }
    const L = use.pieces || [];
    const B = opts.bottoms || {}, labels = opts.labels || {};
    const out = L.map((l, i) => {
      const base = pieces[i] || {};
      const kind = l.kind || base.kind || opts.guess || 'washer';
      const w = l.w || base.w, bodyH = l.h || base.body || base.h;
      if (!w || !bodyH) return null;
      const bottom = l.bottom != null ? l.bottom : base.bottom != null ? base.bottom : kind === 'vanity' ? B.vanity : kind === 'mirror' || kind === 'wall' ? B.mirror : kind === 'tall' ? B.tall : 0;
      return { name: l.name || labels[kind] || base.name || kind, kind, w, h: bodyH + basinUp(l.basin), body: bodyH, bottom, above: l.above != null ? l.above : (l.kind ? false : !!base.above), optional: l.optional != null ? l.optional : (l.kind ? false : !!base.optional), layout: l };
    }).filter(Boolean);
    return out.length ? out : pieces;
  };

  window.NtFitDraw = { parseColors, finishTones, parseSpec, buildPieces, applyLayout, draw };
})();
